import { useEffect, useRef, useState } from "react";
import { SCENE } from "../../portalContent";
import { useInView, usePrefersReducedMotion } from "../../hooks/usePortal";

const ADVANCE_MS = 4200;
const WAVE_COLOR = { welcome: "#f2c98a", class: "#ff5a68", cooldown: "#f2c98a", none: "transparent" };

// An illustrative room. It shows what the studio does for a member; it is not live data.
export default function RoomScene() {
  const wrap = useRef(null);
  const inView = useInView(wrap);
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [held, setHeld] = useState(false);
  const phase = SCENE.phases[index];

  useEffect(() => {
    if (!inView || held || reduced) return undefined;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % SCENE.phases.length), ADVANCE_MS);
    return () => window.clearInterval(timer);
  }, [inView, held, reduced]);

  const select = (i) => { setHeld(true); setIndex(i); };
  const waves = WAVE_COLOR[phase.music];

  return (
    <div ref={wrap} className="pt-scene" onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)}>
      <div className="pt-scene-stage">
        <svg viewBox="0 0 640 360" role="img" aria-label={`Illustration of the studio room: ${phase.label}`}>
          <defs>
            <linearGradient id="pt-cone" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#ffe2a8" stopOpacity="0.85" />
              <stop offset="1" stopColor="#ffb870" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="pt-floor" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#1a1410" />
              <stop offset="1" stopColor="#0b0907" />
            </linearGradient>
          </defs>
          <rect width="640" height="360" fill="#0b0a0a" />
          <rect y="286" width="640" height="74" fill="url(#pt-floor)" />
          <line x1="0" y1="286" x2="640" y2="286" stroke="#2a2420" />
          <g style={{ opacity: phase.lights, transition: "opacity 1.2s ease" }}>
            <polygon points="300,52 340,52 520,286 120,286" fill="url(#pt-cone)" />
            <ellipse cx="320" cy="292" rx="190" ry="14" fill="#ffb870" opacity="0.28" />
          </g>
          <rect x="296" y="36" width="48" height="16" rx="8" fill="#f4ece0" opacity={0.35 + phase.lights * 0.65} style={{ transition: "opacity 1.2s ease" }} />
          {[150, 280, 410].map((x) => (
            <g key={x} stroke="#c9bfb2" strokeWidth="3" fill="none" opacity="0.85">
              <rect x={x} y="246" width="96" height="14" rx="7" />
              <line x1={x + 10} y1="260" x2={x + 10} y2="284" /><line x1={x + 86} y1="260" x2={x + 86} y2="284" />
              <rect x={x + 8} y="236" width="26" height="10" rx="5" />
            </g>
          ))}
          <g transform="translate(60 120)">
            <rect width="36" height="70" rx="8" fill="#171313" stroke="#3a322d" />
            <circle cx="18" cy="24" r="8" fill="#0b0a0a" stroke="#4a403a" />
            <circle cx="18" cy="50" r="12" fill="#0b0a0a" stroke="#4a403a" />
            {[1, 2, 3].map((n) => (
              <path key={`${phase.id}-${n}`} className="pt-wave" style={{ "--n": n }} d={`M ${46 + n * 14} ${70 - n * 14} Q ${60 + n * 18} 85 ${46 + n * 14} ${100 + n * 14}`} stroke={waves} strokeWidth="3" fill="none" strokeLinecap="round" />
            ))}
          </g>
          <g transform="translate(520 40)" aria-hidden="true">
            {[0, 1, 2].map((n) => (
              <circle key={n} cx={n * 20} cy="0" r="5" fill={phase.falters && n === 1 ? "#f2b441" : "#6fd3f7"} opacity={phase.falters && n === 1 ? 0.55 : 1} style={{ transition: "fill .6s, opacity .6s" }} />
            ))}
          </g>
          <text x="22" y="40" className="pt-scene-clock">{phase.clock}</text>
        </svg>
        <div className="pt-scene-caption" aria-live="polite">
          <strong>{phase.label}</strong>
          <span>{phase.body}</span>
        </div>
      </div>
      <div className="pt-scene-steps" role="tablist" aria-label="A class, start to finish">
        {SCENE.phases.map((p, i) => (
          <button key={p.id} role="tab" type="button" aria-selected={i === index} className={i === index ? "is-active" : ""} onClick={() => select(i)} onFocus={() => setHeld(true)}>
            <span>{p.clock}</span>{p.label}
          </button>
        ))}
      </div>
    </div>
  );
}
